import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, of } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { CodigoSefazEnum } from '@shared/enums/financas/codigo-sefaz.enum';
import { TipoContaEnum } from '@shared/enums/financas/tipo-conta.enum';
import { TipoDestinoEnum } from '@shared/enums/financas/tipo-destino.enum';
import { TipoTarifacaoEnum } from '@shared/enums/financas/tipo-tarifacao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { LancarFinanceiroNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/lancar-financeiro-nota-fiscal-saida.request';
import { FinanceiroNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/financeiro-nota-fiscal-saida.response';
import { NFeSaidaPagamentoStruct } from '@shared/services/structs/repo-dfe/nfe-saida-pagamento.struct';
import { NFeSaidaStruct } from '@shared/services/structs/repo-dfe/nfe-saida.struct';
import { ConfigFinancasStruct } from '@shared/services/structs/vendas/config-financas.struct';
import { UtilService } from '@shared/services/util.service';
import {
    calcularParcelasIntermediador,
    dataTarifaSeparada,
    resolverCondicao,
} from '../nfe-pagamento/nfe-pagamento.intermediador';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { diaCivilParaData, instanteParaHoraDaEmpresa, obterDataAtualDaEmpresa } from '@shared/helpers/datetime.util';
import { DiaCivilPipe } from '@shared/pipes/dia-civil.pipe';

export interface NfeLancarFinanceiroDialogData {
    idNFeSaida: number;
    /** "NF-e 123 / 1": o rótulo do cabeçalho. */
    rotuloDocumento: string;
    /** A nota e o bloco `financas`, quando a tela que abriu já os tem; senão o diálogo busca. */
    nota?: NFeSaidaStruct | null;
    financas?: ConfigFinancasStruct | null;
}

/** O que cada recebimento da nota vai gerar no Finanças (regra caixa × título do projects/ATS.Hub.API.Financas/context.md). */
export interface NfeLancarFinanceiroItemVm {
    sequencia: number;
    forma: string;
    categoria: string;
    conta: string | null;
    valor: number;
    /** true = movimentação de entrada na conta; false = título com parcelas. */
    ehMovimentacao: boolean;
    ehBoleto: boolean;
    ehPix: boolean;
    /** Título: as parcelas como o lançamento grava (vencimento pelo prazo do intermediador, taxa, tarifa, líquido, juros). */
    parcelas: { numero: number; vencimento: Date; valor: number; taxa: number | null; tarifa: number | null; liquido: number | null; juros: number | null }[];
    /** Movimentação: uma entrada por parcela, no vencimento de cada uma (à vista é uma só, na data de emissão). */
    entradas: { data: Date; valor: number }[];
    /** Nome do intermediador quando a forma passa por ele, com a taxa (%) da condição comercial. */
    intermediador: string | null;
    taxaPercentual: number | null;
    /** Tarifa lançada separado (tarifação em dia específico, dia da transação ou dia seguinte). */
    tarifaSeparada: { valor: number; data: Date; conta: string } | null;
    totalJuros: number;
    erro: string | null;
}

/**
 * Lançar financeiro da NF-e de saída (T19; cópia de `pv-lancar-financeiro-dialog`, fluxo irmão da venda): mostra o que
 * cada recebimento vai virar no Finanças, pergunta se gera boletos (com o custo de bilhetagem) e chama o orquestrador,
 * que valida tudo de novo. Avisa quando a nota não tem recebimento ou quando eles não fecham com o total (sem troco).
 * Fecha com `true` quando o financeiro foi lançado: a tela que abriu recarrega.
 */
@Component({
    selector: 'app-nfe-lancar-financeiro-dialog',
    standalone: true,
    imports: [
        DiaCivilPipe,
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        AlertCardComponent,
        LoadingBoxComponent,
        AbntPipe,
    ],
    templateUrl: './nfe-lancar-financeiro-dialog.component.html',
    styleUrls: ['./nfe-lancar-financeiro-dialog.component.css'],
})
export class NfeLancarFinanceiroDialogComponent implements OnInit {
    public isLoading = true;
    public isLancando = false;

    public itens: NfeLancarFinanceiroItemVm[] = [];
    public gerarBoletos = true;
    public qtdParcelasBoleto = 0;
    public custoBoletoUnitario = 0;
    public custoBoletoTotal = 0;
    /** Total da nota (o calculado pelo Motor Fiscal e gravado), para conferir com os recebimentos. */
    public totalNota = 0;

    /** Preenchido depois do lançamento quando há boletos ou links Pix a mostrar: o diálogo vira essa lista. */
    public resultado: FinanceiroNotaFiscalSaidaResponse | null = null;

    private nota: NFeSaidaStruct | null = null;
    private financas: ConfigFinancasStruct | null = null;

    private readonly authService = inject(AuthService);

    constructor(
        public dialogRef: MatDialogRef<NfeLancarFinanceiroDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: NfeLancarFinanceiroDialogData,
        private notaFiscalSaidaService: NotaFiscalSaidaService,
        private alertService: AlertService,
    ) {}

    public ngOnInit(): void {
        this.nota = this.data.nota ?? null;
        this.financas = this.data.financas ?? null;

        // Da listagem o diálogo só recebe o id: busca o snapshot da nota e as configurações como o documento faz.
        forkJoin({
            nota: this.nota ? of(null) : this.notaFiscalSaidaService.getVisualizacao(this.data.idNFeSaida),
            config: this.financas ? of(null) : this.notaFiscalSaidaService.getConfiguracoes(),
        }).subscribe({
            next: ({ nota, config }) => {
                if (nota) {
                    if (nota.isError || !nota.nfeSaida) {
                        this.falharCarregamento(nota.errorDescription || 'Nota fiscal não encontrada.');
                        return;
                    }
                    this.nota = nota.nfeSaida;
                }
                if (config) {
                    if (config.isError) {
                        this.falharCarregamento(config.errorDescription || 'Não foi possível carregar as configurações da nota.');
                        return;
                    }
                    this.financas = config.financas ?? null;
                }
                this.montarResumo();
                this.isLoading = false;
            },
            error: () => this.falharCarregamento('Não foi possível carregar a nota agora. Tente novamente em alguns instantes.'),
        });
    }

    public get lancou(): boolean {
        return this.resultado !== null;
    }

    public get temBoleto(): boolean {
        return this.qtdParcelasBoleto > 0;
    }

    public get temItens(): boolean {
        return this.itens.length > 0;
    }

    /** Algum recebimento com intermediador sem condição comercial ou categoria da tarifa: o orquestrador recusaria. */
    public get temErro(): boolean {
        return this.itens.some((i) => !!i.erro);
    }

    public temTaxa(item: NfeLancarFinanceiroItemVm): boolean {
        return item.parcelas.some((p) => p.taxa != null);
    }

    public temTarifaRateada(item: NfeLancarFinanceiroItemVm): boolean {
        return item.parcelas.some((p) => (p.tarifa ?? 0) > 0);
    }

    public temJuros(item: NfeLancarFinanceiroItemVm): boolean {
        return item.totalJuros > 0;
    }

    public get totalRecebimentos(): number {
        return UtilService.arredondarAbnt(this.itens.reduce((soma, i) => soma + i.valor, 0), 2);
    }

    /**
     * Recebimentos que não fecham com o total da nota (sem troco): o orquestrador recusa o lançamento. Mesmas
     * mensagens do servidor, em centavos; vazio quando fecham.
     */
    public get mensagemDiferencaTotal(): string {
        if (!this.temItens) return '';

        const centavos = Math.round(UtilService.arredondarAbnt(this.totalNota - this.totalRecebimentos, 2) * 100);
        if (centavos === 0) return '';

        const diferenca = this.reais(Math.abs(centavos) / 100);
        return centavos > 0
            ? `Faltam ${diferenca} para os recebimentos cobrirem o total da nota de ${this.reais(this.totalNota)}.`
            : `Os recebimentos excedem o total da nota de ${this.reais(this.totalNota)} em ${diferenca}.`;
    }

    /** Motivo do "Lançar financeiro" desabilitado: recebimento com erro ou recebimentos que não fecham com o total. */
    public get tooltipLancar(): string {
        if (this.isLoading || this.isLancando) return '';
        if (this.temErro) return 'Corrija os recebimentos indicados para lançar o financeiro.';
        return this.mensagemDiferencaTotal;
    }

    /** A página pública de cobrança (`/cobranca/:guid`) é desta própria aplicação. */
    public linkCobranca(guid: string): string {
        return `${window.location.origin}/cobranca/${guid}`;
    }

    public copiarLink(guid: string): void {
        navigator.clipboard.writeText(this.linkCobranca(guid)).then(
            () => this.alertService.show('Link copiado', 'Envie o link de cobrança ao cliente.', AlertType.success),
            () => this.alertService.show('Aviso', 'Não foi possível copiar o link. Copie-o manualmente.', AlertType.warning),
        );
    }

    public confirmar(): void {
        if (this.isLancando || !this.temItens || this.temErro || this.mensagemDiferencaTotal) return;

        this.isLancando = true;
        const request = new LancarFinanceiroNotaFiscalSaidaRequest();
        request.gerarBoletos = this.temBoleto && this.gerarBoletos;

        this.notaFiscalSaidaService.lancarFinanceiro(this.data.idNFeSaida, request).subscribe({
            next: (res) => {
                this.isLancando = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível lançar o financeiro da nota.', AlertType.warning);
                    return;
                }

                // Lançou: o retorno vai no toast; o aviso (boleto não gerado, registro na nota pendente) no de aviso.
                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Financeiro lançado', 'O financeiro da nota foi lançado no Finanças.', AlertType.success);
                }

                // Só fica aberto para mostrar os boletos e os links Pix a copiar.
                const boletos = res.boletosGerados;
                const temBoletos = !!boletos && boletos.sucesso + boletos.falha + boletos.semIntegracao > 0;
                if (temBoletos || (res.linksCobranca?.length ?? 0) > 0) {
                    this.resultado = res;
                    return;
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isLancando = false;
                this.alertService.show('Aviso', 'Não foi possível lançar o financeiro agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(this.lancou);
    }

    private falharCarregamento(mensagem: string): void {
        this.isLoading = false;
        this.alertService.show('Aviso', mensagem, AlertType.warning);
        this.dialogRef.close(false);
    }

    private reais(valor: number): string {
        return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    /**
     * Regra caixa × título, a mesma do Finanças: destino "Contas a receber" -> título; destino de conta com conta Caixa
     * ou Conta bancária -> movimentação de entrada; conta de Recebimento/Cartão -> título; sem conta (crediário) -> título
     * sem conta. Nas formas que o intermediador atende, a conta é a dele, como o Finanças impõe ao lançar.
     */
    private montarResumo(): void {
        if (!this.nota || !this.financas) return;

        const formas = this.financas.listaFormaRecebimento ?? [];
        const contas = this.financas.listaContaFinanceira ?? [];
        const categorias = this.financas.listaCategoria ?? [];
        const intermediadores = this.financas.listaIntermediadorPagamento ?? [];
        const condicoesPagamento = this.financas.listaCondicaoPagamento ?? [];
        const custoUnit = UtilService.arredondarAbnt(this.financas.valorBilhetagemBoleto ?? 0, 2);
        // A emissão (instante) vira o dia da empresa e os vencimentos (dias civis) viram Date de parede, para as contas
        // de prazo e para a exibição.
        const timezone = this.authService.getUserInfos().timezone;
        const hoje = obterDataAtualDaEmpresa(timezone);
        const emissao = instanteParaHoraDaEmpresa(this.nota.dataEmissao, timezone) ?? hoje;
        const diaEmissao = new Date(emissao.getFullYear(), emissao.getMonth(), emissao.getDate());

        this.totalNota = UtilService.arredondarAbnt(Number(this.nota.total?.valorTotalNFe ?? this.nota.valorTotal ?? 0) || 0, 2);

        this.itens = [...(this.nota.pagamentos ?? [])]
            .sort((a, b) => a.numeroPagamento - b.numeroPagamento)
            .map((pagamento: NFeSaidaPagamentoStruct) => {
                const forma = formas.find((f) => f.id === pagamento.idFormaRecebimento) ?? null;
                const codigo = String(forma?.codigoPagamentoSefaz ?? '');
                const formaDeIntermediador = [CodigoSefazEnum.CartaoCredito, CodigoSefazEnum.CartaoDebito, CodigoSefazEnum.Boleto, CodigoSefazEnum.Pix].includes(codigo as CodigoSefazEnum);
                const intermediador = formaDeIntermediador && pagamento.idIntermediadorPagamento
                    ? intermediadores.find((i) => i.idIntermediadorPagamento === pagamento.idIntermediadorPagamento) ?? null
                    : null;
                const idConta = intermediador ? intermediador.idContaFinanceira : pagamento.idContaFinanceira;
                const conta = idConta ? contas.find((c) => c.idContaFinanceira === idConta) ?? null : null;
                const idCategoria = pagamento.idCategoriaFinanceira ?? forma?.idCategoriaFinanceira ?? null;
                const categoria = categorias.find((c) => c.id === idCategoria)?.descricao ?? 'Não informada';

                const ehMovimentacao = !intermediador && !!forma && forma.idTipoDestino !== TipoDestinoEnum.ContasAReceber && !!conta
                    && (conta.idTipoConta === TipoContaEnum.ContaCaixa || conta.idTipoConta === TipoContaEnum.ContaBancaria);
                // À vista (sem parcelas): uma parcela única na data de emissão, com o valor do recebimento.
                const valorRecebimento = UtilService.arredondarAbnt(pagamento.valorPagamento ?? 0, 2);
                const base = (pagamento.parcelas ?? []).length > 0
                    ? [...pagamento.parcelas].sort((a, b) => a.numeroParcela - b.numeroParcela)
                        .map((p) => ({ numeroParcela: p.numeroParcela, vencimento: diaCivilParaData(p.dataVencimento) as Date, valor: p.valor }))
                    : [{ numeroParcela: 1, vencimento: diaEmissao, valor: valorRecebimento }];

                const condicaoPagamento = pagamento.idCondicaoPagamento
                    ? condicoesPagamento.find((c) => c.idCondicaoPagamento === pagamento.idCondicaoPagamento) ?? null
                    : null;
                const taxaJuros = condicaoPagamento?.taxaJuros ?? 0;
                const juros = (valor: number) => (taxaJuros > 0 ? UtilService.arredondarAbnt(valor * taxaJuros / 100, 2) : null);

                let parcelas: NfeLancarFinanceiroItemVm['parcelas'] = [];
                let tarifaSeparada: NfeLancarFinanceiroItemVm['tarifaSeparada'] = null;
                let taxaPercentual: number | null = null;
                let erro: string | null = null;

                if (intermediador) {
                    const cond = resolverCondicao(intermediador, forma?.id, pagamento.idBandeira, base.length);

                    if (!cond) {
                        erro = `O intermediador "${intermediador.descricao}" não tem condição comercial para esta forma em ${base.length}x.`;
                    } else {
                        const tarifa = UtilService.arredondarAbnt(cond.tarifa ?? 0, 2);
                        const rateada = tarifa > 0 && intermediador.idTipoTarifacao === TipoTarifacaoEnum.DiaDoPagamento;
                        const calculadas = calcularParcelasIntermediador(base, diaEmissao, cond.prazo, cond.taxa ?? 0, rateada ? tarifa : 0, intermediador.considerarProximoDiaUtil);
                        const dataTarifa = tarifa > 0 ? dataTarifaSeparada(intermediador.idTipoTarifacao, intermediador.diaDebito, hoje, intermediador.considerarProximoDiaUtil) : null;

                        taxaPercentual = cond.taxa ?? 0;
                        parcelas = calculadas.map((p) => ({ numero: p.numeroParcela, vencimento: p.vencimento, valor: p.valor, taxa: p.taxa, tarifa: rateada ? p.tarifa : null, liquido: p.liquido, juros: juros(p.valor) }));
                        tarifaSeparada = dataTarifa ? { valor: tarifa, data: dataTarifa, conta: intermediador.descricaoContaFinanceira ?? intermediador.descricao } : null;
                        if (dataTarifa && !intermediador.idCategoriaFinanceiraTarifa) erro = `O intermediador "${intermediador.descricao}" não tem categoria para a tarifa.`;
                    }
                } else if (!ehMovimentacao) {
                    parcelas = base.map((p) => ({ numero: p.numeroParcela, vencimento: p.vencimento, valor: p.valor, taxa: null, tarifa: null, liquido: null, juros: juros(p.valor) }));
                }

                return {
                    sequencia: pagamento.numeroPagamento,
                    forma: forma?.descricao ?? pagamento.descricaoFormaRecebimento ?? 'Forma não informada',
                    categoria,
                    conta: conta?.descricao ?? null,
                    valor: valorRecebimento,
                    ehMovimentacao,
                    ehBoleto: !intermediador && !ehMovimentacao && !!conta && codigo === CodigoSefazEnum.Boleto,
                    ehPix: !intermediador && !ehMovimentacao && !!conta && codigo === CodigoSefazEnum.Pix,
                    parcelas,
                    entradas: ehMovimentacao ? base.map((p) => ({ data: p.vencimento, valor: p.valor })) : [],
                    intermediador: intermediador?.descricao ?? null,
                    taxaPercentual,
                    tarifaSeparada,
                    totalJuros: UtilService.arredondarAbnt(parcelas.reduce((s, p) => s + (p.juros ?? 0), 0), 2),
                    erro,
                };
            });

        this.qtdParcelasBoleto = this.itens.filter((i) => i.ehBoleto).reduce((soma, i) => soma + i.parcelas.length, 0);
        this.custoBoletoUnitario = custoUnit;
        this.custoBoletoTotal = UtilService.arredondarAbnt(this.qtdParcelasBoleto * custoUnit, 2);
    }
}
